import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { adminAuthApi, AdminUser, AdminAuthResponse } from '../api/admin-auth.api';
import { requestAdminPushPermissionAndSubscribe } from '../services/admin-push.service';

interface AdminAuthState {
  admin: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AdminAuthResponse>;
  logout: () => Promise<void>;
  fetchAdmin: () => Promise<void>;
  clearAuth: () => void;
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set) => ({
      admin: null,
      token: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await adminAuthApi.login({ email, password });
        set({
          admin: response.admin,
          token: response.token,
          isAuthenticated: true,
        });
        localStorage.setItem('admin_token', response.token);
        // Register background push immediately after login
        requestAdminPushPermissionAndSubscribe().catch(() => {});
        return response;
      },

      logout: async () => {
        try {
          await adminAuthApi.logout();
        } catch (_) {
          // ignore logout errors
        }
        set({
          admin: null,
          token: null,
          isAuthenticated: false,
        });
        localStorage.removeItem('admin_token');
      },

      fetchAdmin: async () => {
        try {
          const admin = await adminAuthApi.me();
          set({ admin, isAuthenticated: true });
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            set({ admin: null, token: null, isAuthenticated: false });
            localStorage.removeItem('admin_token');
          }
        }
      },

      clearAuth: () => {
        set({ admin: null, token: null, isAuthenticated: false });
        localStorage.removeItem('admin_token');
      },
    }),
    {
      name: 'admin-auth-storage',
    }
  )
);
